// TS bridge to the custom LocationBG Swift plugin.
//
// On native iOS (once the plugin is compiled into the build), this drives CoreLocation directly
// with allowsBackgroundLocationUpdates = true, so GPS keeps tracking while the phone is locked.
// On web / pre-plugin builds, the registerPlugin proxy returns a stub whose methods reject —
// callers detect that and fall back to the foreground-only @capacitor/geolocation watcher.
//
// v4.9 adds a native durable queue and a JS drain helper
// (`drainNativeLocationQueue`) so points captured while the WebView is
// suspended/coalesced are recovered after wake.
import { registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

export type BGLocationPing = {
  lat: number;
  lng: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  capturedAt: string;
  /**
   * Best-effort source disambiguation. iOS does not directly expose the cause
   * of a CLLocation delivery; native infers based on whether a
   * `requestLocation()` was in flight. Older shells emit `native_ios_sigchange`.
   */
  source:
    | "native_ios_background"
    | "native_ios_heartbeat"
    | "native_ios_sigchange";
};


export type LocationBGAuthStatus =
  | "always"
  | "whenInUse"
  | "denied"
  | "restricted"
  | "prompt"
  | "unknown";

export type LocationBGStartArgs = {
  /** Meters. Native clamps to 5–2000 m. Default 250 if omitted (older shells). */
  distanceFilter?: number;
  /** When this much time passes with no movement, native widens the heartbeat cadence. */
  stoppedAfterMs?: number;
  /** Drives the heartbeat cadence (clamped 30–120s active, up to 600s stopped). */
  stoppedIntervalMs?: number;
};

export type LocationBGStartResult = {
  ok: true;
  distanceFilter?: number;
  stoppedAfterMs?: number;
  stoppedIntervalMs?: number;
  heartbeatCadenceMs?: number;
};

export type NativeQueuedPoint = {
  id: string;
  timestamp: string; // ISO
  lat: number;
  lng: number;
  horizontalAccuracy: number;
  speed?: number;
  course?: number;
  altitude?: number;
  source: string;
  createdAt: string; // ISO
  deliveredToJs: boolean;
};

/** Lightweight queue stats — see LocationBGPlugin.getQueueStats. */
export type NativeQueueStats = {
  native_queue_count: number;
  oldest_queued_point_at: string | null;
  newest_queued_point_at: string | null;
  last_drain_attempt_at: string | null;
  last_drain_success_at: string | null;
  /** Build 30+ diagnostics — undefined on older shells. */
  tracking_active?: boolean;
  heartbeat_cadence_ms?: number;
  last_heartbeat_fire_at?: string | null;
  last_queue_persisted_at?: string | null;
  last_drain_request_emitted_at?: string | null;
};


export interface LocationBGPlugin {
  getAuthStatus(): Promise<{ status: LocationBGAuthStatus }>;
  requestAlways(): Promise<{ status: LocationBGAuthStatus }>;
  startWatch(args?: LocationBGStartArgs): Promise<LocationBGStartResult>;
  stopWatch(): Promise<{ ok: true }>;
  getQueuedLocationPoints(args?: { limit?: number }): Promise<{ points: NativeQueuedPoint[] }>;
  markQueuedLocationPointsDelivered(args: { ids: string[] }): Promise<{ ok: true; marked: number }>;
  clearDeliveredLocationPoints(): Promise<{ ok: true; removed: number }>;
  /** v5.6+. Cheap stats read — does NOT dequeue or drain. */
  getQueueStats(): Promise<NativeQueueStats>;
  addListener(
    eventName: "locationUpdate",
    cb: (p: BGLocationPing) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "locationError",
    cb: (e: { message: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "authStatus",
    cb: (s: { status: LocationBGAuthStatus }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "queueDrainRequested",
    cb: (e: { count: number }) => void,
  ): Promise<PluginListenerHandle>;
}

export const LocationBG = registerPlugin<LocationBGPlugin>("LocationBG");

import { Capacitor } from "@capacitor/core";
import { isPluginRegistered, probePlugin, type PluginProbeResult } from "./plugin-inventory";
import { sendOrQueuePing, type QueuedPing } from "./ping-outbox";
import { bumpGpsFixTimestamp } from "./gps-status";

/** True when the Swift plugin is compiled into this build (registration only — no side effects). */
export function locationBGRegistered(): boolean {
  return isPluginRegistered("LocationBG");
}

/** Full diagnostic: registration + safe getAuthStatus probe. */
export async function probeLocationBG(): Promise<PluginProbeResult> {
  return probePlugin("LocationBG", () => LocationBG.getAuthStatus(), "getAuthStatus()");
}

/** @deprecated kept for back-compat — prefer locationBGRegistered() / probeLocationBG(). */
export async function locationBGAvailable(): Promise<boolean> {
  return locationBGRegistered();
}

// ---------------------------------------------------------------------------
// Native queue drain (v4.9)
// ---------------------------------------------------------------------------

const DELIVERED_LRU_KEY = "roava.nativeQueue.delivered";
const DELIVERED_LRU_CAP = 2000;

function loadDeliveredLRU(): Set<string> {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(DELIVERED_LRU_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch { /* ignore */ }
  return new Set();
}

function saveDeliveredLRU(set: Set<string>): void {
  if (typeof localStorage === "undefined") return;
  // Trim to cap (FIFO — Set iteration order = insertion order).
  if (set.size > DELIVERED_LRU_CAP) {
    const arr = Array.from(set);
    const trimmed = arr.slice(arr.length - DELIVERED_LRU_CAP);
    set.clear();
    for (const id of trimmed) set.add(id);
  }
  try {
    localStorage.setItem(DELIVERED_LRU_KEY, JSON.stringify(Array.from(set)));
  } catch { /* quota — ignore */ }
}

function normalizeForOutbox(
  p: NativeQueuedPoint,
  tripId: string,
): QueuedPing {
  return {
    tripId,
    lat: p.lat,
    lng: p.lng,
    accuracy: Number.isFinite(p.horizontalAccuracy) ? p.horizontalAccuracy : null,
    altitude: typeof p.altitude === "number" ? p.altitude : null,
    speed: typeof p.speed === "number" ? p.speed : null,
    heading: typeof p.course === "number" ? p.course : null,
    capturedAt: p.timestamp,
    source: "native_ios_background",
    // Deterministic idempotency key. Native generates `p.id` (UUID) once per
    // queued point; reusing it across drains lets the server unique index
    // collapse duplicate inserts caused by racing drains.
    clientPointKey: `native:${p.id}`,
  };
}

// Single-flight guard. Concurrent callers (network restore + foreground +
// route mount + manual Sync now) all share the same in-flight Promise instead
// of spawning parallel drains that would each re-enqueue the same native
// points before any of them mark them delivered.
let inFlightDrain: Promise<{ drained: number; skipped: number }> | null = null;

/**
 * Pull queued points from the native side and feed them into the JS outbox.
 *
 * - Returns immediately on web / pre-plugin shells.
 * - Dedupes by native id against an LRU stored in localStorage (cap 2000).
 * - Within-batch dedupe on `timestamp|lat|lng` keeps the best-accuracy fix.
 * - If `tripId` is null, points are marked delivered (to drain the backlog after
 *   tracking stops) but NOT uploaded. Caller can pass null after stopWatch.
 * - Points with `horizontalAccuracy > 100` are still uploaded but logged.
 * - Only marks IDs delivered for points that successfully reached
 *   `sendOrQueuePing` (which either uploads or queues to IndexedDB).
 */
export async function drainNativeLocationQueue(
  tripId: string | null,
): Promise<{ drained: number; skipped: number }> {
  if (inFlightDrain) {
    // eslint-disable-next-line no-console
    console.info("[LocationBG] drain join — concurrent call awaiting in-flight drain");
    return inFlightDrain;
  }
  const run = (async () => {
    try {
      return await drainNativeLocationQueueImpl(tripId);
    } finally {
      inFlightDrain = null;
    }
  })();
  inFlightDrain = run;
  return run;
}

async function drainNativeLocationQueueImpl(
  tripId: string | null,
): Promise<{ drained: number; skipped: number }> {
  if (!Capacitor.isNativePlatform() || !locationBGRegistered()) {
    return { drained: 0, skipped: 0 };
  }

  let res: { points: NativeQueuedPoint[] };
  try {
    res = await LocationBG.getQueuedLocationPoints({ limit: 500 });
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.warn(`[LocationBG] drain getQueuedLocationPoints failed: ${e?.message ?? e}`);
    return { drained: 0, skipped: 0 };
  }

  const all = res?.points ?? [];
  if (all.length === 0) return { drained: 0, skipped: 0 };

  const lru = loadDeliveredLRU();

  // Filter out points we've already processed and points already marked
  // delivered on the native side (they'll be auto-purged shortly).
  const freshRaw = all.filter((p) => !p.deliveredToJs && !lru.has(p.id));

  // ----------------------------------------------------------------
  // HARD QUALITY GATE — runs BEFORE dedupe, upload, outbox, route or
  // map handling. Low-confidence / stale points are dropped and marked
  // delivered so they never reappear in a future drain.
  // ----------------------------------------------------------------
  const STALE_MS = 10 * 60_000;
  const MAX_ACC_M = 250;
  const nowTs = Date.now();
  const droppedLowAcc: string[] = [];
  const droppedStale: string[] = [];
  const fresh: NativeQueuedPoint[] = [];

  for (const p of freshRaw) {
    if (Number.isFinite(p.horizontalAccuracy) && p.horizontalAccuracy > MAX_ACC_M) {
      console.info(`[LocationBG] drop low-confidence acc=${p.horizontalAccuracy.toFixed(0)}m id=${p.id}`);
      droppedLowAcc.push(p.id);
      continue;
    }
    const tsMs = Date.parse(p.timestamp);
    if (Number.isFinite(tsMs) && nowTs - tsMs > STALE_MS) {
      console.info(`[LocationBG] drop stale timestamp=${p.timestamp} ageMs=${nowTs - tsMs} id=${p.id}`);
      droppedStale.push(p.id);
      continue;
    }
    fresh.push(p);
  }

  // Within-batch dedupe: best-accuracy per (timestamp|lat|lng).
  const bestByKey = new Map<string, NativeQueuedPoint>();
  for (const p of fresh) {
    const key = `${p.timestamp}|${p.lat.toFixed(6)}|${p.lng.toFixed(6)}`;
    const prev = bestByKey.get(key);
    if (!prev || p.horizontalAccuracy < prev.horizontalAccuracy) {
      bestByKey.set(key, p);
    }
  }
  const candidates = Array.from(bestByKey.values());
  const dedupedIds = fresh.filter((p) => !bestByKey.has(`${p.timestamp}|${p.lat.toFixed(6)}|${p.lng.toFixed(6)}`) ||
    bestByKey.get(`${p.timestamp}|${p.lat.toFixed(6)}|${p.lng.toFixed(6)}`)?.id !== p.id).map((p) => p.id);

  // If no trip context, just mark every fresh id delivered to clear the backlog.
  if (!tripId) {
    const ids = [...fresh.map((p) => p.id), ...droppedLowAcc, ...droppedStale];
    if (ids.length > 0) {
      // eslint-disable-next-line no-console
      console.info(`[LocationBG] drain skip — no tripId, marking ${ids.length} ids delivered`);
      try {
        await LocationBG.markQueuedLocationPointsDelivered({ ids });
        for (const id of ids) lru.add(id);
        saveDeliveredLRU(lru);
      } catch (e: any) {
        console.warn(`[LocationBG] markDelivered failed: ${e?.message ?? e}`);
      }
    }
    return { drained: 0, skipped: ids.length };
  }

  const delivered: string[] = [];
  let skipped = dedupedIds.length + droppedLowAcc.length + droppedStale.length;

  for (const p of candidates) {
    try {
      await sendOrQueuePing(normalizeForOutbox(p, tripId));
      delivered.push(p.id);
    } catch (e: any) {
      // sendOrQueuePing already falls back to IndexedDB internally; any throw
      // means even the local queue write failed — leave the native point
      // alone so the next drain re-tries.
      // eslint-disable-next-line no-console
      console.warn(`[LocationBG] drain sendOrQueuePing failed id=${p.id}: ${e?.message ?? e}`);
      skipped += 1;
    }
  }

  // Mark within-batch duplicates AND quality-rejected points delivered —
  // duplicates have a better twin already sent; rejects must not loop
  // through future drains as if they were real route points.
  const idsToMark = [...delivered, ...dedupedIds, ...droppedLowAcc, ...droppedStale];
  if (idsToMark.length > 0) {
    try {
      await LocationBG.markQueuedLocationPointsDelivered({ ids: idsToMark });
      for (const id of idsToMark) lru.add(id);
      saveDeliveredLRU(lru);
    } catch (e: any) {
      console.warn(`[LocationBG] markDelivered failed: ${e?.message ?? e}`);
    }
  }

  // Advance GPS health timestamp from the newest ACCEPTED point only. A
  // stale or low-confidence reject must not refresh "Last GPS fix".
  let newestTs = 0;
  for (const p of fresh) {
    const t = Date.parse(p.timestamp);
    if (Number.isFinite(t) && t > newestTs) newestTs = t;
  }
  if (newestTs > 0) bumpGpsFixTimestamp(newestTs);

  return { drained: delivered.length, skipped };
}


// Fire-and-forget startup probe so every native launch prints a definitive
// state into the Xcode console. Web no-ops. Idempotent guard prevents double-run
// if this module gets re-imported by HMR.
declare global {
  // eslint-disable-next-line no-var
  var __ROAVA_LOCATIONBG_PROBED__: boolean | undefined;
}
if (Capacitor.isNativePlatform() && !globalThis.__ROAVA_LOCATIONBG_PROBED__) {
  globalThis.__ROAVA_LOCATIONBG_PROBED__ = true;
  void probeLocationBG().then(
    (r) => {
      // eslint-disable-next-line no-console
      console.info(
        `[LocationBG] probe state=${r.state}${r.errorMessage ? ` error=${r.errorMessage}` : ""} method=${r.probedMethod ?? "?"} registryHint=${isPluginRegistered("LocationBG")}`,
      );
    },
    (e) => {
      // eslint-disable-next-line no-console
      console.error(`[LocationBG] probe threw: ${e?.message ?? String(e)}`);
    },
  );
}

/**
 * Cheap native queue stats — count + oldest/newest queued + last drain
 * attempt/success timestamps. Does NOT dequeue. Returns null on web or
 * on pre-v5.6 binaries that don't have getQueueStats.
 */
export async function getNativeQueueStats(): Promise<NativeQueueStats | null> {
  if (!Capacitor.isNativePlatform() || !locationBGRegistered()) return null;
  try {
    const res = await (LocationBG as any).getQueueStats();
    if (!res || typeof res !== "object") return null;
    const pickStr = (v: unknown): string | null | undefined =>
      typeof v === "string" ? v : v === null ? null : undefined;
    return {
      native_queue_count: Number(res.native_queue_count ?? 0),
      oldest_queued_point_at:
        typeof res.oldest_queued_point_at === "string" ? res.oldest_queued_point_at : null,
      newest_queued_point_at:
        typeof res.newest_queued_point_at === "string" ? res.newest_queued_point_at : null,
      last_drain_attempt_at:
        typeof res.last_drain_attempt_at === "string" ? res.last_drain_attempt_at : null,
      last_drain_success_at:
        typeof res.last_drain_success_at === "string" ? res.last_drain_success_at : null,
      // Build 30+ optional diagnostics — pass through only if present so
      // older shells leave them undefined for consumers to detect.
      tracking_active:
        typeof res.tracking_active === "boolean" ? res.tracking_active : undefined,
      heartbeat_cadence_ms:
        typeof res.heartbeat_cadence_ms === "number" ? res.heartbeat_cadence_ms : undefined,
      last_heartbeat_fire_at: pickStr(res.last_heartbeat_fire_at),
      last_queue_persisted_at: pickStr(res.last_queue_persisted_at),
      last_drain_request_emitted_at: pickStr(res.last_drain_request_emitted_at),
    };
  } catch {
    return null;
  }
}
